import { useState, useEffect } from "react";
import { ListTreeIcon, CheckCircle2, ExternalLink, FilterIcon, Star, Archive, Trash2, Copy, PlusCircle, BarChart4 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { Progress } from "@/components/ui/progress";
import { Confetti } from "@/components/ui/confetti";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { motion, AnimatePresence } from "framer-motion";
import { cn, copyFormattedContent } from "@/lib/utils";

interface Directory {
  id: string;
  name: string;
  url: string;
  sort_order?: number | null;
  created_at?: string;
  updated_at?: string;
  completed?: boolean;
  date_submitted?: string;
  submission_count?: number;
}

interface DirectorySubmission {
  id: string;
  directory_id: string;
  project_id: string | null;
  date_submitted: string;
}

const DirectoriesPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { id: projectId } = useParams();
  const queryClient = useQueryClient();
  const [showConfetti, setShowConfetti] = useState(false);
  const [reachedMilestone, setReachedMilestone] = useState(false);
  const [activeTab, setActiveTab] = useState<"pending" | "completed">("pending");
  const [copiedDirectoryId, setCopiedDirectoryId] = useState<string | null>(null);

  // Fetch all directories
  const { data: directories = [], isLoading: isLoadingDirectories } = useQuery<Directory[]>({
    queryKey: ['directories'],
    queryFn: async () => {
      // Call the database function directly with raw SQL instead of using the RPC method
      // since the RPC function appears to be missing
      const { data, error } = await supabase
        .from('directories')
        .select('*')
        .order('sort_order', { ascending: true, nullsFirst: false });
      
      if (error) {
        console.error('Error fetching directories:', error);
        toast({
          title: "Error",
          description: "Could not load directories",
          variant: "destructive",
        });
        throw error;
      }
      
      return data || [];
    },
  });

  // Fetch project's directory submissions
  const { data: submissions = [], isLoading: isLoadingSubmissions } = useQuery<DirectorySubmission[]>({
    queryKey: ['directory_submissions', projectId],
    enabled: !!user && !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('directory_submissions')
        .select('*')
        .eq('user_id', user?.id)
        .eq('project_id', projectId);
      
      if (error) {
        console.error('Error fetching directory submissions:', error);
        toast({
          title: "Error",
          description: "Could not load your submissions",
          variant: "destructive",
        });
        throw error;
      }
      
      return data as DirectorySubmission[];
    },
  });

  // Create directory submission mutation
  const createSubmissionMutation = useMutation({
    mutationFn: async ({ directoryId }: { directoryId: string }) => {
      if (!projectId) {
        throw new Error("No project selected");
      }

      const { error } = await supabase
        .from('directory_submissions')
        .insert({
          directory_id: directoryId,
          user_id: user?.id,
          project_id: projectId,
          date_submitted: new Date().toISOString(),
        });

      if (error) {
        toast({
          title: "Error",
          description: "Could not mark directory as completed",
          variant: "destructive",
        });
        throw error;
      }
      
      toast({
        title: "Success",
        description: "Directory marked as completed for this project",
        variant: "success",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directory_submissions', projectId] });
    },
  });

  const handleMarkComplete = (directoryId: string) => {
    createSubmissionMutation.mutate({ directoryId });
    
    // Check if this completion will reach a milestone
    const currentCompleted = submissions.length;
    const milestones = [5, 10, 15, 20];
    const nextMilestone = milestones.find(m => m > currentCompleted);
    
    if (nextMilestone && currentCompleted + 1 === nextMilestone) {
      setReachedMilestone(true);
      setShowConfetti(true);
    }
  };

  // Close confetti after it's done
  const handleConfettiComplete = () => {
    setShowConfetti(false);
  };

  const isLoading = isLoadingDirectories || isLoadingSubmissions;

  // Create a map of completed directories for efficient lookup
  const submittedDirectoryIds = new Set(submissions.map(sub => sub.directory_id));

  // Group directories by categories (let's use a simple grouping since we don't have category field)
  const categorizedDirectories = directories.reduce<Record<string, Directory[]>>((acc, dir) => {
    // Using a generic category since our schema doesn't have a category field
    const category = "Product Directories";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(dir);
    return acc;
  }, {});

  // Filter directories by submission status
  const pendingDirectories = directories.filter(dir => !submittedDirectoryIds.has(dir.id));
  const completedDirectories = directories.filter(dir => submittedDirectoryIds.has(dir.id));
  const completedCount = completedDirectories.length;
  const totalCount = directories.length;
  const completionPercentage = Math.round((completedCount / totalCount) * 100) || 0;

  // Format date to readable format
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch (e) {
      return dateString;
    }
  };

  // Handle copying a directory URL to clipboard
  const handleCopyUrl = (directory: Directory) => {
    if (!directory?.url) {
      toast({
        title: "Copy Failed",
        description: "No URL available to copy",
        variant: "destructive",
      });
      return;
    }
    
    copyFormattedContent(directory.url, {
      onSuccess: () => {
        setCopiedDirectoryId(directory.id);
        toast({
          title: "URL Copied",
          description: "Directory URL has been copied to clipboard",
        });
        setTimeout(() => setCopiedDirectoryId(null), 2000);
      },
      onError: (error) => {
        console.error("Copy failed:", error);
        toast({
          title: "Copy Failed",
          description: "Failed to copy URL to clipboard",
          variant: "destructive",
        });
      }
    });
  };

  // Show error if no project is selected
  if (!projectId) {
    return (
      <div className="container max-w-7xl mx-auto py-6">
        <Card className="p-8 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
            <ListTreeIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-medium mb-2">No Project Selected</h3>
          <p className="text-muted-foreground max-w-md">
            Please select a project to track directory submissions.
          </p>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container max-w-7xl mx-auto py-6">
        <div className="flex flex-col space-y-6">
          <div className="flex items-center justify-between">
            <div className="h-14 w-72 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse"></div>
            <div className="h-10 w-48 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse"></div>
          </div>
          <div className="h-32 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-800 dark:to-gray-700 rounded-xl animate-pulse"></div>
          <div className="grid gap-4">
            <div className="h-10 bg-gray-200 dark:bg-gray-800 rounded-lg w-1/4 animate-pulse"></div>
            <div className="h-64 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  // Simple importance badge (since we don't have importance field in schema)
  const getImportanceBadge = () => {
    return <Badge variant="secondary" className="flex gap-1 items-center"><Star className="h-3 w-3" /> Standard</Badge>;
  };

  return (
    <div className="container max-w-7xl mx-auto py-6 space-y-6">
      {/* Confetti animation for milestones */}
      {showConfetti && (
        <Confetti
          duration={4000}
          onComplete={handleConfettiComplete}
          particleCount={300}
          spread={180}
        />
      )}
      
      {/* Header section with title and progress overview */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight gradient-text">
            Directory Submissions
          </h1>
          <p className="text-muted-foreground mt-1">
            Submit your product to these directories to increase visibility and reach
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm" 
            className={cn(
              "border-none", 
              activeTab === "pending" ? "bg-background" : "bg-transparent"
            )}
            onClick={() => setActiveTab("pending")}
          >
            <ListTreeIcon className="h-4 w-4 mr-2" />
            Pending 
            <Badge variant="secondary" className="ml-2">{pendingDirectories.length}</Badge>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "border-none",
              activeTab === "completed" ? "bg-background" : "bg-transparent"
            )}
            onClick={() => setActiveTab("completed")}
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Completed
            <Badge variant="secondary" className="ml-2">{completedDirectories.length}</Badge>
          </Button>
        </div>
      </div>
      
      {/* Progress card with summary metrics */}
      <Card className="border-0 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-950/30 dark:to-blue-950/30 p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <BarChart4 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                  Directory Submission Progress
                </h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Listing your product in these directories will improve visibility and attract more potential customers
              </p>
              
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm font-medium">
                  <span>Project progress</span>
                  <span className={completionPercentage === 100 ? "text-green-600 dark:text-green-400" : "text-blue-600 dark:text-blue-400"}>
                    {completedCount}/{totalCount} directories
                  </span>
                </div>
                <Progress 
                  value={completionPercentage} 
                  className="h-2 bg-blue-100 dark:bg-blue-950/30"
                  indicatorClassName={completionPercentage === 100 ? "bg-green-500" : "bg-blue-600"}
                />
              </div>
            </div>
            
            <div className="flex flex-col gap-2 min-w-[150px] text-center p-4 rounded-xl bg-background shadow-sm border border-border">
              <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">{completionPercentage}%</span>
              <span className="text-xs text-muted-foreground">Completion Rate</span>
              <Separator className="my-1" />
              <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span>{completedCount} completed</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Tips and best practices card */}
      <Card className="bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-950/30 dark:to-blue-950/30 border-blue-200 dark:border-blue-900/30">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 flex items-center gap-2 mb-3">
            <ListTreeIcon className="h-5 w-5" />
            Directory Submission Tips
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/70 dark:bg-gray-800/50 p-4 rounded-lg border border-blue-100 dark:border-blue-900/20">
              <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Quality Over Quantity</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">Focus on high-quality directory listings relevant to your industry rather than submitting to every directory.</p>
            </div>
            <div className="bg-white/70 dark:bg-gray-800/50 p-4 rounded-lg border border-blue-100 dark:border-blue-900/20">
              <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Consistent Information</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">Keep your business name, description, and contact information consistent across all directory listings.</p>
            </div>
          </div>
        </div>
      </Card>
      
      {/* If milestone reached, show celebration message */}
      <AnimatePresence>
        {reachedMilestone && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10">
              <div className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-700/30">
                  <CheckCircle2 className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="font-medium text-amber-800 dark:text-amber-300">Milestone achieved!</h3>
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    You've completed {completedCount} directory submissions. Keep up the great work!
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Directory listings - tabbed view for pending/completed */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Directories</CardTitle>
          <CardDescription>
            Submit to these directories to increase your product's visibility
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="pending" value={activeTab} onValueChange={(value) => setActiveTab(value as "pending" | "completed")}>
            <TabsList className="mb-4">
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <ListTreeIcon className="h-4 w-4" />
                Pending ({pendingDirectories.length})
              </TabsTrigger>
              <TabsTrigger value="completed" className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Completed ({completedDirectories.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="pending">
              <div className="space-y-4">
                {Object.entries(categorizedDirectories).map(([category, dirs]) => {
                  const categoryDirs = dirs.filter(dir => !submittedDirectoryIds.has(dir.id));
                  if (categoryDirs.length === 0) return null;
                  
                  return (
                    <Card key={category} className="overflow-hidden border">
                      <div className="p-4 border-b bg-muted/30 flex items-center justify-between">
                        <h3 className="font-medium flex items-center gap-2">
                          <FilterIcon className="h-4 w-4 text-muted-foreground" />
                          {category}
                        </h3>
                        <Badge variant="outline">{categoryDirs.length}</Badge>
                      </div>
                      <div className="divide-y">
                        {categoryDirs.map((directory) => (
                          <div 
                            key={directory.id} 
                            className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:bg-muted/10 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="font-medium text-foreground mb-0.5">{directory.name}</div>
                                  <a 
                                    href={directory.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 truncate"
                                  >
                                    {directory.url}
                                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                  </a>
                                </div>
                                <div className="flex-shrink-0">
                                  {getImportanceBadge()}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMarkComplete(directory.id)}
                                className="text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:border-green-900/50 dark:hover:bg-green-900/20"
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                                Mark Complete
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleCopyUrl(directory)}
                              >
                                <Copy className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
                
                {/* Empty state for pending */}
                {pendingDirectories.length === 0 && (
                  <Card className="p-8 flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">All directories submitted!</h3>
                    <p className="text-muted-foreground max-w-md">
                      Great work! You've submitted to all recommended directories.
                      This will help increase your product's visibility.
                    </p>
                  </Card>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="completed">
              <div className="space-y-4">
                {Object.entries(categorizedDirectories).map(([category, dirs]) => {
                  const categoryDirs = dirs.filter(dir => submittedDirectoryIds.has(dir.id));
                  if (categoryDirs.length === 0) return null;
                  
                  return (
                    <Card key={category} className="overflow-hidden border">
                      <div className="p-4 border-b bg-muted/30 flex items-center justify-between">
                        <h3 className="font-medium flex items-center gap-2">
                          <FilterIcon className="h-4 w-4 text-muted-foreground" />
                          {category}
                        </h3>
                        <Badge variant="outline">{categoryDirs.length}</Badge>
                      </div>
                      <div className="divide-y">
                        {categoryDirs.map((directory) => {
                          const submission = submissions.find(s => s.directory_id === directory.id);
                          
                          return (
                            <div 
                              key={directory.id} 
                              className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-muted/20"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <div className="font-medium text-foreground mb-0.5">{directory.name}</div>
                                    <a 
                                      href={directory.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 truncate"
                                    >
                                      {directory.url}
                                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                    </a>
                                  </div>
                                  <div className="flex-shrink-0">
                                    {getImportanceBadge()}
                                  </div>
                                </div>
                                
                                <div className="mt-2 flex items-center text-sm text-green-600 dark:text-green-400">
                                  <CheckCircle2 className="h-4 w-4 mr-1" />
                                  <span>Submitted on {formatDate(submission?.date_submitted || "")}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  );
                })}
                
                {/* Empty state for completed */}
                {completedDirectories.length === 0 && (
                  <Card className="p-8 flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                      <ListTreeIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">No completed submissions yet</h3>
                    <p className="text-muted-foreground max-w-md">
                      Start submitting your product to directories to increase visibility.
                      Switch to the Pending tab to see available directories.
                    </p>
                  </Card>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      {/* Empty state if no directories at all */}
      {directories.length === 0 && (
        <Card className="p-8 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <ListTreeIcon className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-medium mb-2">No directories found</h3>
          <p className="text-muted-foreground max-w-md">
            Check back later for our recommended directories.
          </p>
        </Card>
      )}
    </div>
  );
};

export default DirectoriesPage;
